import { FormikProvider, useFormik } from 'formik';
import { ReactNode, useMemo, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router';
import { Box, Flex } from 'reflexbox';

import { useFeature, useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { QuillText, Separator, Snippet, Title } from '@treams/treams-ui';

import ArchiveButton from '../packages/kakapo-ui-kit/components/common/ArchiveButton';
import DetailsTableForm from '../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import TalentReviewChanges from './TalentReviewChanges';
import TalentReviewGrid from './TalentReviewGrid';
import TalentReviewMotivations from './TalentReviewMotivations';
import TalentReviewProgress from './TalentReviewProgress';
import TalentReviewTeam from './TalentReviewTeam';
import { TalentReviewUndo } from './TalentReviewUndo';

export default function TalentReviewV1(props: {
	backButton?: ReactNode;
	review: graphql.TalentReview;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { isWorkspaceSuperuser } = useViewerRoles();

	const hideOwnerInTalentReview = useFeature(graphql.FeatureName.HideOwnerInTalentReview);

	const users = useMemo(() => {
		return props.review.group.members.edges
			.filter(({ role }) => role.type !== graphql.RoleType.Owner || !hideOwnerInTalentReview)
			.map(({ node }) => node as graphql.User);
	}, [props.review.group.members.edges, hideOwnerInTalentReview]);

	const [lastTitle, setLastTitle] = useState(props.review.title);
	const [changeCount, setChangeCount] = useState(0);
	const [dragging, setDragging] = useState(false);

	const [temporaryPlacements, setTemporaryPlacements] = useState(props.review.placements);

	const [update, { loading }] = graphql.useUpdateTalentReviewMutation();

	const formik = useFormik({
		initialValues: {
			title: props.review.title,
		},
		onSubmit: () => {},
	});

	const titleChanged = formik.values.title !== lastTitle;

	async function onUpdate(placement: graphql.TalentReviewPlacement) {
		const old = temporaryPlacements.find((match) => match.user.id === placement.user.id);

		if (!old) {
			return;
		}

		if (old.column !== placement.column || old.row !== placement.row) {
			setTemporaryPlacements([
				placement,
				...temporaryPlacements.filter((match) => match.user.id !== placement.user.id),
			]);
		} else {
			setTemporaryPlacements(
				temporaryPlacements.map((match) =>
					match.user.id === placement.user.id ? placement : match,
				),
			);
		}

		if (
			(old.motivation !== placement.motivation || old.score !== placement.score) &&
			changeCount === 0
		) {
			setChangeCount(1);
		}
	}

	async function onUpdateMotivation(userID: string, motivation: string) {
		setTemporaryPlacements(
			temporaryPlacements.map((placement) => {
				if (placement.user.id === userID) {
					return { ...placement, motivation };
				} else {
					return placement;
				}
			}),
		);

		if (changeCount === 0) {
			setChangeCount(1);
		}
	}

	async function onSave(status: graphql.TalentReviewStatus) {
		setLastTitle(formik.values.title);
		setChangeCount(0);

		await update({
			variables: {
				input: {
					reviewID: props.review.id,
					title: formik.values.title,
					placements: temporaryPlacements.map(({ row, column, score, user, motivation }) => ({
						row,
						column,
						score,
						userID: user.id,
						motivation,
					})),
					status,
				},
			},
		});
	}

	return (
		<Box mr={5}>
			<Flex my={3}>
				<Title>
					{props.review.viewerCanUpdate ? t('talentReviews.edit.title') : props.review.title}
				</Title>

				{props.backButton}
			</Flex>

			{!props.review.viewerCanUpdate && props.review.template.description && (
				<Snippet>
					<QuillText value={localize(props.review.template.description).text} />
				</Snippet>
			)}

			<Box mb={3}>
				<Separator />
			</Box>

			{props.review.viewerCanUpdate && (
				<>
					<Box maxWidth={600}>
						<FormikProvider value={formik}>
							<DetailsTableForm
								formValues={[
									{
										label: t('talentReviews.edit.titleLabel'),
										name: 'title',
										type: DetailsTableFormValueTypes.Text,
									},
								]}
							/>
						</FormikProvider>
					</Box>
					{props.review.template.description && (
						<Snippet>
							<QuillText value={localize(props.review.template.description).text} />
						</Snippet>
					)}
					<Box my={3}>
						<Separator />
					</Box>
					<TalentReviewProgress review={props.review} placements={temporaryPlacements} />

					<Box mt={3}>
						<Prompt
							when={changeCount > 0 || titleChanged}
							message={t('talentReviews.edit.unsavedChanges')}
						/>
						<TalentReviewChanges
							count={changeCount + (titleChanged ? 1 : 0)}
							loading={loading}
							onSave={onSave}
						/>
					</Box>
				</>
			)}

			{isWorkspaceSuperuser && props.review.status === graphql.TalentReviewStatus.Shared && (
				<TalentReviewUndo {...props.review} />
			)}
			<DragDropContext
				onDragStart={() => setDragging(true)}
				onDragEnd={(result) => {
					setDragging(false);

					if (!result.destination?.droppableId.startsWith('cell-')) {
						return;
					}

					const motivation = temporaryPlacements.find(
						(placement) => placement.user.id === result.draggableId,
					)?.motivation;

					const placements = temporaryPlacements.filter(
						({ user }) => user.id !== result.draggableId,
					);

					const [row, column] = result.destination.droppableId
						.split('-')
						.slice(1)
						.map((x) => parseInt(x));

					const cell = placements.filter(
						(placement) => placement.row === row && placement.column === column,
					);

					const user = users.find((user) => user.id === result.draggableId)!;

					cell.splice(result.destination.index, 0, {
						user,
						row,
						column,
						motivation,
					});

					setTemporaryPlacements(
						placements
							.filter((placement) => !(placement.row === row && placement.column === column))
							.concat(cell),
					);

					setChangeCount(changeCount + 1);
				}}
			>
				{props.review.viewerCanUpdate && (
					<TalentReviewTeam
						temporaryPlacements={temporaryPlacements}
						users={users}
						readonly={!props.review.viewerCanUpdate}
					/>
				)}
				<TalentReviewGrid
					dragging={dragging}
					onUpdateMotivation={onUpdateMotivation}
					temporaryPlacements={temporaryPlacements}
					users={users}
					template={props.review.template}
					readonly={!props.review.viewerCanUpdate}
				/>
			</DragDropContext>
			<TalentReviewMotivations
				review={props.review}
				placements={temporaryPlacements}
				onUpdate={onUpdate}
			/>
			<Flex>
				<ArchiveButton
					item={props.review}
					useArchive={graphql.useArchiveTalentReviewMutation}
					useRestore={graphql.useRestoreTalentReviewMutation}
				/>
			</Flex>
		</Box>
	);
}
